import React, { useState, useEffect } from 'react';
import { devError } from '@/lib/logger';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Clock, LogOut, FileText, Calendar, User } from 'lucide-react';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';
import { format, subDays } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PortalDashboard() {
  const navigate = useNavigate();
  const [employee, setEmployee] = useState(null);
  const [records, setRecords] = useState([]);
  const [todayRecord, setTodayRecord] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const checkAuth = async () => {
    try {
      const res = await fetch(`${API}/portal/me`, { credentials: 'include' });
      if (!res.ok) {
        navigate('/portal');
        return;
      }
      const data = await res.json();
      setEmployee(data);
      fetchRecords(data.employee_id);
    } catch (err) { devError("op", err);
      navigate('/portal');
    }
  };

  const fetchRecords = async (employeeId) => {
    const today = new Date().toISOString().split('T')[0];
    const startDate = format(subDays(new Date(), 30), 'yyyy-MM-dd');
    
    try {
      const res = await fetch(
        `${API}/time-records?employee_id=${employeeId}&start_date=${startDate}&end_date=${today}`,
        { credentials: 'include' }
      );
      const data = await res.json();
      setRecords(data);
      
      const todayRec = data.find(r => r.date === today);
      setTodayRecord(todayRec || null);
    } catch (error) {
      toast.error('Erro ao carregar registros');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API}/portal/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (err) {
      devError('portal-logout', err);
    }
    navigate('/portal');
  };

  const formatTime = (isoString) => {
    if (!isoString) return '--:--';
    try {
      const date = new Date(isoString);
      return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    } catch (err) { devError("op", err);
      return '--:--';
    }
  };

  const statusLabels = {
    not_started: 'Não Iniciado',
    working: 'Trabalhando',
    on_break: 'Em Pausa',
    completed: 'Concluído'
  };

  const statusColors = {
    not_started: 'bg-[#9CA3AF]',
    working: 'bg-[#059669]',
    on_break: 'bg-[#F59E0B]',
    completed: 'bg-[#111111]'
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
        <div className="w-8 h-8 border-2 border-[#111111] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Toaster position="top-right" />
      
      {/* Header */}
      <header className="border-b border-[#E5E7EB] bg-white px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#111111] flex items-center justify-center">
              <Clock className="w-6 h-6 text-white" />
            </div>
            <div>
              <span className="text-lg font-black tracking-tight block" style={{ fontFamily: 'Satoshi, sans-serif' }}>
                Portal do Colaborador
              </span>
              <span className="text-sm text-[#6B7280]">{employee?.name}</span>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <Link to="/portal/requests">
              <Button variant="outline" size="sm" data-testid="nav-requests">
                <FileText className="w-4 h-4 mr-2" />
                Solicitações
              </Button>
            </Link>
            <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="portal-logout-btn">
              <LogOut className="w-4 h-4 mr-2" />
              Sair
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-6">
        {/* Today's Status */}
        <section className="mb-8">
          <h2 className="text-xs uppercase tracking-widest text-[#6B7280] mb-4">Hoje</h2>
          <div className="border border-[#E5E7EB] bg-white p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <p className="text-2xl font-black" style={{ fontFamily: 'Satoshi, sans-serif' }}>
                  {format(new Date(), "EEEE, dd 'de' MMMM", { locale: ptBR })}
                </p>
                <p className="text-[#6B7280]">
                  Status: {' '}
                  <span className={`badge ${statusColors[todayRecord?.status || 'not_started']} text-white ml-1`}>
                    {statusLabels[todayRecord?.status || 'not_started']}
                  </span>
                </p>
              </div>
              {todayRecord?.is_late && (
                <span className="badge bg-[#DC2626] text-white">Atraso</span>
              )}
            </div>
            
            <div className="grid grid-cols-4 gap-4">
              <TimeCard label="Entrada" time={formatTime(todayRecord?.clock_in)} />
              <TimeCard label="Início Pausa" time={formatTime(todayRecord?.break_start)} />
              <TimeCard label="Fim Pausa" time={formatTime(todayRecord?.break_end)} />
              <TimeCard label="Saída" time={formatTime(todayRecord?.clock_out)} />
            </div>
            
            {/* Hours today */}
            {todayRecord && (
              <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t border-[#E5E7EB]">
                <TimeCard label="Horas Brutas" time={todayRecord.worked_display || '--:--'} />
                <TimeCard label="Tempo Pausa" time={todayRecord.break_display || '--:--'} highlight="amber" />
                <TimeCard label="Horas Líquidas" time={todayRecord.net_display || '--:--'} highlight="blue" />
              </div>
            )}
          </div>
        </section>

        {/* History */}
        <section>
          <h2 className="text-xs uppercase tracking-widest text-[#6B7280] mb-4">
            Histórico (Últimos 30 dias)
          </h2>
          <div className="border border-[#E5E7EB] overflow-x-auto">
            <table className="data-table bg-white">
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Entrada</th>
                  <th>Início Pausa</th>
                  <th>Fim Pausa</th>
                  <th>Saída</th>
                  <th>Horas Líq.</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {records.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center py-8 text-[#6B7280]">
                      Nenhum registro encontrado
                    </td>
                  </tr>
                ) : (
                  records.map(record => (
                    <tr key={record.record_id} data-testid={`portal-record-${record.record_id}`}>
                      <td className="font-medium">
                        {format(new Date(record.date + 'T00:00:00'), "dd/MM/yyyy (EEE)", { locale: ptBR })}
                      </td>
                      <td className="mono">{formatTime(record.clock_in)}</td>
                      <td className="mono">{formatTime(record.break_start)}</td>
                      <td className="mono">{formatTime(record.break_end)}</td>
                      <td
                        className={`mono ${record.is_auto_closed ? 'text-[#DC2626] font-bold' : ''}`}
                        title={record.is_auto_closed ? 'Saída registada por fechamento compulsório' : ''}
                      >
                        {formatTime(record.clock_out)}
                        {record.is_auto_closed ? ' *' : ''}
                      </td>
                      <td className="mono font-bold text-[#0029FF]">{record.net_display || '-'}</td>
                      <td>
                        <span className={`badge ${statusColors[record.status]} text-white`}>
                          {statusLabels[record.status]}
                        </span>
                        {record.is_late && (
                          <span className="badge bg-[#DC2626] text-white ml-1">Atraso</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>
      </main>
    </div>
  );
}

function TimeCard({ label, time, highlight }) {
  const colorMap = { blue: 'text-[#0029FF]', amber: 'text-[#F59E0B]' };
  return (
    <div className="text-center p-4 bg-[#F3F4F6]">
      <p className="text-xs uppercase tracking-widest text-[#6B7280] mb-1">{label}</p>
      <p className={`text-2xl font-bold mono ${colorMap[highlight] || ''}`} style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
        {time}
      </p>
    </div>
  );
}
